import { Observable } from 'rxjs';
import { StreamActivityNotificationRI } from 'common-utils/rest-interfaces';
import {pdsLogger} from '../pds-logger';

// To enable logging, on browser console window, type:
//   localStorage.setItem('PDS-LOGGER-get-stream-cache', 'true');
// To disable loging, either:
//   localStorage.setItem('PDS-LOGGER-get-stream-cache', 'false');
//   localStorage.removeItem('PDS-LOGGER-get-stream-cache');
let getStreamCachelog = pdsLogger.requiresLogger('get-stream-cache');

interface IGetSteamCache {
  limit: number;
  timestamp: number;
  description: string;
  observable: Observable<any>;
}

let getStreamCache = new Map<string, IGetSteamCache>();
let enableCaching = true;
let maxCacheDuration = 300000; // Default of 5 minutes
getStreamCachelog('GetStreamCache: maxCacheDuration = ' + maxCacheDuration + '(default)');

/**
 * Getstream Caching.  This is intended to be accessed only through Getstream-related functionality within PDS.
 */
export class GetStreamCacheService {
  static getObservable(feedUrl: string, limit: number): Observable<any> | undefined {
    if (enableCaching) {
      let cacheItem = getStreamCache.get(feedUrl);
      if (cacheItem) {
        if (cacheItem.limit === limit) {
          let now = Date.now();
          if (now - cacheItem.timestamp < maxCacheDuration) {
            getStreamCachelog('GetStreamCache(' + getStreamCache.size + ', ' + feedUrl + '): [Cache Hit] - ' + cacheItem.description);
            return cacheItem.observable;
          }
        }
        getStreamCache.delete(feedUrl);
      }
    }
  }

  static cacheObservable(feedUrl: string, limit: number, cacheDescription: string, observable: Observable<any>): Observable<any> {
    if (enableCaching) {
      getStreamCachelog('GetStreamCache(' + getStreamCache.size + ', ' + feedUrl + '): [Cache Miss] - ' + cacheDescription);
      getStreamCache.set(feedUrl, {
        limit: limit,
        timestamp: Date.now(),
        description: cacheDescription,
        observable: observable
      });
    }
    return observable;
  }

  static evictObservable(feedUrl: string, cacheDescription: string): void {
    if (enableCaching) {
      let prefix = 'GetStreamCache(' + getStreamCache.size + ', ' + feedUrl + '): Evicting...';
      if (getStreamCache.has(feedUrl)) {
        getStreamCache.delete(feedUrl);
        getStreamCachelog(prefix + '[Cache Evicted] - ' + cacheDescription);
      } else {
        getStreamCachelog(prefix + '[Cache Entry Not Found] - ' + cacheDescription);
      }
    }
  }

  static clear(): void {
    getStreamCache.clear();
  }
}

/**
 * Allows external applications to configure GetStreamCacheService by exposing this through
 * pds.services.GetStreamCacheConfig, which is accessible through Browser console as well.
 */
export class GetStreamCacheServiceConfig {
  static get enableCaching(): boolean {
    return enableCaching;
  }

  static set enableCaching(value: boolean) {
    enableCaching = value;
    getStreamCachelog('GetStreamCache: Setting enableCaching to ' + enableCaching);
  }

  static get maxCacheDuration(): number {
    return maxCacheDuration;
  }

  static set maxCacheDuration(value: number) {
    maxCacheDuration = value;
    getStreamCachelog('GetStreamCache: Setting maxCacheDuration to ' + maxCacheDuration);
  }

  /**
   * Static function for debugging purpose.  This can be invoked from the browser console like in UC Portal:
   * pds.services.GetStreamCacheServiceConfig.dump()
   */
  static dump(): void {
    getStreamCachelog('GetStreamCache: Dump', getStreamCache);
  }
}
